import type {
  OnboardingAnalyticsProps,
  OnboardingCardState,
  OnboardingDestination,
  OnboardingNextStep,
  OnboardingPhase,
  OnboardingStep,
} from "@ifgengineering/client-analytics/src/types";
import type { MarketingPreferenceType } from "@ifgengineering/client-invest-sdk";
import { DAY_QUOTED_LOCKOUT_MS } from "./cardState";
import type {
  ActionableRequirementKey,
  DealAnchoredView,
  DealRequirement,
  DealRequirementKey,
} from "./progress";
import type { V2BSection } from "./Shell/journey";
import {
  REQUIREMENT_PATH,
  V2B_FALLBACK_RETURN_PATH,
  V2B_PRE_APP_PATH,
} from "./routes";

/** Where an onboarding event fires: one of the step flows, or a deal page that shows the card. */
export type OnboardingAnalyticsSurface = V2BSection | "dealPage";

const ANALYTICS_STEP: Record<ActionableRequirementKey, OnboardingStep> = {
  taxAndOccupation: "tax_details",
  accreditation: "accreditation",
  quiz: "quiz",
  verification: "verification",
};

export const toAnalyticsStep = (
  key: ActionableRequirementKey
): OnboardingStep => ANALYTICS_STEP[key];

export const toAnalyticsNextStep = (
  key: DealRequirementKey | null | undefined
): OnboardingNextStep =>
  !key || key === "coolDown" ? "cool_down" : ANALYTICS_STEP[key];

/** The rules API puts every pre-app page before the app; everything after it continues in the app. */
export const phaseOf = (surface: OnboardingAnalyticsSurface): OnboardingPhase =>
  surface === "preApp" ? "pre_app" : "in_app_continuation";

export const cardStateOf = ({
  key,
  status,
}: DealRequirement): OnboardingCardState => {
  switch (key) {
    case "taxAndOccupation":
      return "tax_details";
    case "quiz":
      if (status === "locked") return "quiz_locked";
      return status === "in_progress" ? "quiz_retry" : "quiz_not_started";
    case "coolDown":
      return "cool_down";
    default:
      return key;
  }
};

const isDone = ({ status }: DealRequirement): boolean =>
  status === "satisfied" || status === "submitted";

export interface DealProgressAnalytics {
  card_state: OnboardingCardState;
  next_step: OnboardingNextStep;
  completed_step_count: number;
  total_step_count: number;
}

/** The card's position in the investor's steps; counts leave out the cool-down, which is a wait. */
export const dealProgressAnalytics = (
  view: DealAnchoredView,
  activeKey: DealRequirementKey | null
): DealProgressAnalytics => {
  const steps = view.requirements.filter(({ key }) => key !== "coolDown");
  const active = view.requirements.find(({ key }) => key === activeKey);
  return {
    card_state: active ? cardStateOf(active) : "cool_down",
    next_step: toAnalyticsNextStep(activeKey),
    completed_step_count: steps.filter(isDone).length,
    total_step_count: steps.length,
  };
};

const DEAL_PAGE_PATTERN = /^\/app\/deals\/[^/]+$/;
const STEP_PATHS = Object.values(REQUIREMENT_PATH);

export const destinationOf = (path: string): OnboardingDestination => {
  if (path === V2B_PRE_APP_PATH) return "pre_app";
  if (STEP_PATHS.includes(path)) return "step";
  if (path === V2B_FALLBACK_RETURN_PATH) return "deals_list";
  if (DEAL_PAGE_PATTERN.test(path)) return "deal";
  return "homepage";
};

/** What a deal-page event carries of onboarding: the arm, and the deal that anchored it, but no phase or entry point. */
export const armAndAnchorOf = ({
  onboarding_flow,
  onboarding_version,
  anchoring_deal_id,
  anchoring_deal_name,
}: OnboardingAnalyticsProps): OnboardingAnalyticsProps => ({
  ...(onboarding_flow ? { onboarding_flow } : {}),
  ...(onboarding_version ? { onboarding_version } : {}),
  ...(anchoring_deal_id ? { anchoring_deal_id } : {}),
  ...(anchoring_deal_name ? { anchoring_deal_name } : {}),
});

const isAxiosLike = (
  error: unknown
): error is {
  isAxiosError?: boolean;
  code?: string;
  response?: { status?: number };
} => typeof error === "object" && error !== null;

const isNetworkError = (error: unknown): boolean =>
  isAxiosLike(error) &&
  !error.response &&
  (error.code === "ERR_NETWORK" || error.isAxiosError === true);

const httpStatusOf = (error: unknown): number | undefined =>
  isAxiosLike(error) ? error.response?.status : undefined;

export interface SectionFailure {
  reason: "network_error" | "server_error" | "client_error" | "other";
  http_status?: number;
}

/** A failed save as one of a closed set of reasons, never the raw message. */
export const sectionFailureOf = (error: unknown): SectionFailure => {
  if (isNetworkError(error)) return { reason: "network_error" };
  const status = httpStatusOf(error);
  if (status === undefined) return { reason: "other" };
  if (status >= 500) return { reason: "server_error", http_status: status };
  if (status >= 400) return { reason: "client_error", http_status: status };
  return { reason: "other", http_status: status };
};

export type UploadFailureReason =
  | "file_too_large"
  | "unsupported_format"
  | "network_error"
  | "server_error"
  | "other";

/** The KYC upload refuses an oversize file with 413 and a format it cannot take with 400. */
export const uploadFailureReasonOf = (error: unknown): UploadFailureReason => {
  if (isNetworkError(error)) return "network_error";
  const status = httpStatusOf(error);
  if (status === 413) return "file_too_large";
  if (status === 400 || status === 415) return "unsupported_format";
  if (status !== undefined && status >= 500) return "server_error";
  return "other";
};

/** The quiz returns only its pause, whose length says how many misses came before it. */
export const quizAttemptBandOf = (
  blockedUntil: string | null | undefined,
  now: number = Date.now()
): "first_fail" | "second_or_third_fail" | "fourth_plus_fail" => {
  const pauseMs = blockedUntil ? new Date(blockedUntil).getTime() - now : NaN;
  if (Number.isNaN(pauseMs)) return "first_fail";
  return pauseMs > DAY_QUOTED_LOCKOUT_MS
    ? "fourth_plus_fail"
    : "second_or_third_fail";
};

const AGE_BANDS: readonly [number, string][] = [
  [24, "18-24"],
  [34, "25-34"],
  [44, "35-44"],
  [54, "45-54"],
  [64, "55-64"],
];

/** Only the band leaves the browser, never the date of birth. */
export const ageBandOf = (
  dob: string | null | undefined,
  now: Date = new Date()
): string => {
  const birth = dob ? new Date(dob.slice(0, 10)) : null;
  if (!birth || Number.isNaN(birth.getTime())) return "unknown";
  const hasHadBirthday =
    now.getUTCMonth() > birth.getUTCMonth() ||
    (now.getUTCMonth() === birth.getUTCMonth() &&
      now.getUTCDate() >= birth.getUTCDate());
  const age =
    now.getUTCFullYear() - birth.getUTCFullYear() - (hasHadBirthday ? 0 : 1);
  if (age < 18) return "unknown";
  return AGE_BANDS.find(([upTo]) => age <= upTo)?.[1] ?? "65+";
};

const EMAIL_PREFERENCE: Record<MarketingPreferenceType, string> = {
  CUR8: "cur8_capital",
  CUR8_IFG: "cur8_and_ifg",
  NONE: "none",
};

export const emailPreferenceOf = (
  preference: MarketingPreferenceType
): string => EMAIL_PREFERENCE[preference];
